import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Fade,
  useTheme,
  useMediaQuery,
  InputAdornment,
  CircularProgress,
  Alert,
  Badge,
  Paper,
  Container,
  ToggleButtonGroup,
  ToggleButton,
  Drawer,
  Divider,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Slider,
  IconButton,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer as CartDrawer,
  Tabs,
  Tab,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Fab
} from '@mui/material';
import {
  Add,
  ShoppingCart,
  CheckCircle,
  Restaurant,
  Close,
  ExpandMore,
  ViewList,
  ViewModule,
  Remove,
  HideImage,
  Whatshot,
  KeyboardArrowDown,
  Category,
  Tune as TuneIcon,
  Search as SearchIcon,
  DeleteOutline,
  Receipt,
  Dining as DiningIcon,
  Restaurant as TakeawayIcon,
  Edit,
  Person,
  Directions,
  CheckCircleOutline,
  Pending, ArrowBack
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { tableService } from '../../services/tableService';
import { menuService } from '../../services/menuService';
import { buildImageUrl } from '../../utils/imageUtils';
import { useNotification } from '../../contexts/NotificationContext';
import { useRestaurant } from '../../contexts/RestaurantContext';
import MenuItemModal from '../../components/user/MenuItemModal';
import { getAdminUserForTable } from '../../services/adminUserService'
import CommonModal from '../../components/common/CommonModal';
import { getOutletOutOfStocks } from '../../services/stockService';
import { restaurantService } from '../../services/restaurantService';

// ============================================
// Lazy Category Component - From DeliveryPage
// ============================================
const LazyCategory = ({ categoryId, categoryName, isFirst, forceRender, categoryRefs, children }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [hasEverRendered, setHasEverRendered] = useState(false);
  const [recordedHeight, setRecordedHeight] = useState('80px');
  const elementRef = useRef(null);

  useEffect(() => {
    if (isFirst || forceRender) {
      setShouldRender(true);
      setHasEverRendered(true);
    }
  }, [isFirst, forceRender]);

  useEffect(() => {
    if (isFirst) return;

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setShouldRender(true);
            setHasEverRendered(true);
          } else if (hasEverRendered && !forceRender) {
            const rect = entry.boundingClientRect;
            const viewportHeight = window.innerHeight;
            if (rect.bottom < -viewportHeight * 1.5 || rect.top > viewportHeight * 2.5) {
              if (elementRef.current) {
                setRecordedHeight(`${elementRef.current.getBoundingClientRect().height}px`);
              }
              setShouldRender(false);
            }
          }
        });
      },
      { rootMargin: '400px 0px 400px 0px', threshold: 0.01 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [hasEverRendered, forceRender, isFirst]);

  const setRef = (el) => {
    elementRef.current = el;
    if (categoryRefs && categoryRefs.current) {
      categoryRefs.current[categoryId] = el;
    }
  };

  return (
    <Box
      ref={setRef}
      id={`category-${categoryId}`}
      sx={{
        minHeight: shouldRender ? 'auto' : recordedHeight,
        transition: 'all 0.2s ease',
        scrollMarginTop: '120px',
        mb: { xs: 4, sm: 6, md: 8 }
      }}
    >
      {shouldRender ? (
        <Fade in timeout={300}>
          <Box>{children}</Box>
        </Fade>
      ) : (
        <Box 
          sx={{ 
            height: recordedHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
          }}
        >
          {hasEverRendered ? null : (
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              {categoryName}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
// Move hook outside component
const useIntersectionObserver = (setActiveCategory, categories, categoryRefs) => {
  useEffect(() => {
    const observers = {};
    const options = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    };

    categories.forEach((category) => {
      const element = categoryRefs.current[category.id];
      if (element) {
        observers[category.id] = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveCategory(category.id);
            }
          });
        }, options);
        observers[category.id].observe(element);
      }
    });

    return () => {
      Object.values(observers).forEach((observer) => observer.disconnect());
    };
  }, [categories, setActiveCategory, categoryRefs]);
};

const TableOrderPage = () => {
  const { uniqueString } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableOrderStatus, setTableOrderStatus] = useState(false);
  const [confirmItems, setConfirmItems] = useState(false)

  // Menu related state
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [tableOrderId, setTableOrderId] = useState(null);
  const [forcedVisibleCategories, setForcedVisibleCategories] = useState(new Set());

  // Order form state
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('counter');
  const [specialInstructions, setSpecialInstructions] = useState('');

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('grid');
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [vegFilter, setVegFilter] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showScrollPrompt, setShowScrollPrompt] = useState(true);
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(JSON.parse(sessionStorage.getItem('cart_draw')));
  const [cartLoading, setCartLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [adminName, setAdminName] = useState("Guest")
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [paymentReference, setPaymentReference] = useState("")
  const [statusTab, setStatusTab] = useState(JSON.parse(localStorage.getItem('user_id')) ? 'confirmed' : 'pending');

  // Payment modal state
  // const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [balance, setBalance] = useState(""); // Add this state
  const [outOfStockItems, setOutOfStockItems] = useState({})
  const [restaurantData, setRestaurantData] = useState({})

  // New state for Dine-in/Takeaway toggle
  const [orderTypeSelection, setOrderTypeSelection] = useState('dinein'); // 'dinein' or 'takeaway'
  const [cartItems, setCartItems] = useState({ dinein: [], takeaway: [] });
  const [tableName, setTableName] = useState("")
  const [activeCartTab, setActiveCartTab] = useState('dinein'); // 'dinein', 'takeaway', 'both'
  const [cartValues, setCartValues] = useState({
    "cart_total": 0,
    "tax": 0,
    "service_charge": 0,
    "service_charge_value": 0,
    "tax_value": 0,
    "total": 0,
  })

  const categoryRefs = useRef({});

  const { showNotification, showSuccess, showError } = useNotification();
  const {
    cart,
    addToCart,
    getCartItemCount,
    getCartTotal,
    updateCartItem,
    removeFromCart,
    clearCart,
  } = useRestaurant();

  async function fetchOutOfStockItems(selectedOutlet) {
    try {
      const response = await getOutletOutOfStocks(selectedOutlet);
      setOutOfStockItems(response)
    }
    catch (error) {
      console.error("Error on Getting Out of Items ", error)
    }
  }



  const isItemOutOfStock = (item) => {
    // Check if item is in outOfStockItems.menu_item_ids
    if (outOfStockItems?.menu_item_ids?.includes(item.id)) {
      return true;
    }

    // Check if item's category is in outOfStockItems.category_ids
    // if (outOfStockItems?.category_ids?.includes(item.category_id)) {
    //   return true;
    // }

    return false;
  };


  const getOutOfStockStyles = (isOutOfStock) => {
    if (!isOutOfStock) return {};

    return {
      filter: 'grayscale(1)',
      opacity: 0.6,
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        zIndex: 1,
        pointerEvents: 'none',
      }
    };
  };

  const fetchRestaurantData = async () => {
    try {
      const response = await restaurantService.getPublicRestaurantData()
      // console.log(response)
      setRestaurantData(response)
    }
    catch (error) {
      console.error("Error While Retrieving Restaurant Data", error)
    }
  }

  // Fetch table cart items
  const fetchTableCartItems = useCallback(async () => {
    if (!tableData?.table?.id) return;

    try {
      setCartLoading(true);
      const response = await tableService.getTableCartItems(tableData.table.id);
      // console.log("Table Cart Items => ", response.data.data);

      const cartData = response.data.data;
      const dineInItems = cartData.dine_in?.items || [];
      const takeawayItems = cartData.takeaway?.items || [];

      setCartItems({
        dinein: dineInItems,
        takeaway: takeawayItems
      });

      setTableName(response?.data?.table_name)

      setCartValues(
        {
          "cart_total": cartData?.cart_total,
          "tax": cartData?.tax,
          "tax_value": cartData?.tax_value,
          "service_charge": cartData?.service_charge,
          "service_charge_value": cartData?.service_charge_value,
          "total": cartData?.final_total,
        }
      )

      // Sync with local cart for UI consistency
      clearCart();
      [...dineInItems, ...takeawayItems].forEach(item => {
        addToCart(
          { id: item.menu_item_id, name: item.name, base_price: item.base_price, is_veg: item.is_veg },
          item.quantity,
          item.addons || [],
          item.instructions || '',
          item.size
        );
      });

      return { dinein: dineInItems, takeaway: takeawayItems };
    } catch (error) {
      console.error("Error fetching cart items => ", error);
      return { dinein: [], takeaway: [] };
    } finally {
      setCartLoading(false);
    }
  }, [tableData, clearCart, addToCart, confirmItems]);

  // Check table order status
  const checkTableOrderStatus = useCallback(async () => {
    if (!tableData?.table?.id) return null;

    try {
      const response = await tableService.tableOrderStatusCheck({ table_id: tableData.table.id });
      // console.log("Order status response => ", response);
      setTableOrderStatus(response.data.ordering_status);
      if (response.data.order_id) {
        setTableOrderId(response.data.order_id);
      }
      if (response.data.status && response.data.status === "closed") {
        navigate(`/table-checkout/${uniqueString}`)
      }
      return response;
    } catch (error) {
      console.error("Error checking order status => ", error);
      return null;
    }
  }, [tableData]);


  useEffect(() => {
    if (selectedPayment === "Cash" && amountReceived) {
      const received = parseFloat(amountReceived) || 0;
      const total = cartValues.total || 0;
      const calculatedBalance = received - total;
      setBalance(calculatedBalance >= 0 ? calculatedBalance.toFixed(2) : "0.00");
    } else {
      setBalance("");
    }
  }, [amountReceived, selectedPayment, cartValues.total]);

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const tableResponse = await tableService.getTableOrderPage(uniqueString);
        setTableData(tableResponse);

        const [categoriesResponse, menuResponse] = await Promise.all([
          restaurantService.getPublicCategoriesByOutlet(tableResponse.outlet.id),
          menuService.getPublicMenuItems(tableResponse.outlet.id),
        ]);

        fetchOutOfStockItems(tableResponse.outlet.id)
        setCategories(categoriesResponse);
        setMenuItems(menuResponse);

        if (categoriesResponse.length > 0) {
          setSelectedCategory(categoriesResponse[0].id);
          setActiveCategory(categoriesResponse[0].id);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Table not found or link is invalid');
      } finally {
        setLoading(false);
      }
    };

    const fetchAdminData = async () => {
      try {
        let admin_id = sessionStorage.getItem('user_id')
        const response = await getAdminUserForTable({ "user_id": admin_id })
        // console.log("Admin Response", response.result.username)
        setAdminName(response.result.username)
      }
      catch (error) {
        console.error(error)
      }
    }

    if (uniqueString) {
      fetchData();
      fetchRestaurantData()
    }

    if (sessionStorage.getItem("user_id") || sessionStorage.getItem("user_id") !== null) {
      fetchAdminData()
    }

  }, [uniqueString]);


  // After tableData loads, check status and fetch cart
  useEffect(() => {
    const initializePage = async () => {
      if (tableData) {
        setIsInitializing(true);
        await checkTableOrderStatus();
        await fetchTableCartItems();
        setIsInitializing(false);
      }
    };

    initializePage();
  }, [tableData, checkTableOrderStatus, fetchTableCartItems]);

  useIntersectionObserver(setActiveCategory, categories, categoryRefs);

  // Set initial price range when menu items load
  useEffect(() => {
    if (menuItems.length > 0) {
      const prices = menuItems.map((item) => item.base_price);
      setPriceRange([Math.min(...prices), Math.max(...prices)]);
    }
  }, [menuItems]);

  useEffect(() => {
    const timer = setTimeout(() => setShowScrollPrompt(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const scrollToCategory = (id) => {
  setSelectedCategory(id);
  setActiveCategory(id);

  // CRITICAL: Force render the target category and all between
  const targetIndex = categories.findIndex(c => c.id === id);
  const categoriesToRender = new Set();
  
  // Render target and 2 categories before/after for smooth experience
  for (let i = Math.max(0, targetIndex - 2); i <= Math.min(categories.length - 1, targetIndex + 2); i++) {
    if (categories[i]) {
      categoriesToRender.add(categories[i].id);
    }
  }

  // Update visibility state to force render these categories
  setForcedVisibleCategories(categoriesToRender);

  // Wait for render, then scroll
  setTimeout(() => {
    const el = categoryRefs.current[id];
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Clear forced visibility after scroll completes
      setTimeout(() => {
        setForcedVisibleCategories(new Set());
      }, 1000);
    }
  }, 100);

  if (isMobile) setCategoriesDialogOpen(false);
};

  // FIXED: Handle quantity change with sizes and addons
  const handleQuantityChange = async (item_id, quantity, size_name = null, addons = null, order_type = null) => {
    // console.log("Item ID => ", item_id);
    // console.log("Quantity => ", quantity);
    // console.log("Size Name => ", size_name);
    // console.log("Addons => ", addons);
    // console.log("Order Type => ", order_type);
    if (quantity === 0) {
      handleRemoveItem(item_id, order_type)
    }
    else {
      // Ensure size_name is a string
      let sizeNameString = null;
      if (size_name) {
        // If size_name is an object with name property, extract it
        sizeNameString = typeof size_name === 'object' ? size_name.name : size_name;
      }

      let payload = {
        "order_id": tableOrderId,
        "menu_item_id": item_id,
        "quantity": quantity,
        "size_name": sizeNameString,
        "addons": addons || [],
        "order_type": order_type || orderTypeSelection.toUpperCase()
      };

      // console.log("Quantity Update Payload => ", payload);

      try {
        const response = await tableService.handleQuantityChange(payload);
        await fetchTableCartItems();
        showSuccess("Quantity updated");
      } catch (error) {
        console.error("Error While Quantity Update => ", error);
        showError("Failed to update quantity");
      }
    }
  };

  const handleAddToCart = async (item) => {
    // console.log("Adding item => ", item);
    // console.log("Order Type Selection => ", orderTypeSelection);

    let outlet_id = tableData.outlet.id;
    let isAdmin = JSON.parse(sessionStorage.getItem('user_id'))
    if (item.addons_with_details?.length === 0 && item.sizes_with_prices?.length === 0) {
      let tableCartItem = {
        table_id: tableData.table.id,
        outlet_id: outlet_id,
        menu_item_id: item.id,
        name: item.name,
        description: item.description,
        image_url: item.image_url,
        base_price: item.base_price,
        quantity: 1,
        total_price: item.base_price,
        is_veg: item.is_veg,
        category_id: item.category_id,
        discount_percentage: item.discount_percentage,
        order_type: orderTypeSelection.toUpperCase(),
        status: isAdmin ? true : false
      };

      let currentOrderId = tableOrderId;
      let currentOrderStatus = tableOrderStatus;

      if (!currentOrderId || !currentOrderStatus) {
        // console.log("Order info missing, fetching fresh status...");
        const statusResponse = await tableService.tableOrderStatusCheck({ table_id: tableData.table.id });
        currentOrderStatus = statusResponse.data.ordering_status;
        currentOrderId = statusResponse.data.order_id;

        if (currentOrderId) {
          setTableOrderId(currentOrderId);
          setTableOrderStatus(currentOrderStatus);
        }
      }

      // console.log("currentOrderStatus => ", currentOrderStatus);
      // console.log("currentOrderId => ", currentOrderId);

      if (!currentOrderStatus) {
        try {
          // console.log("Creating new order...");
          const response = await tableService.newTableOrder(tableCartItem);
          setTableOrderStatus(true);
          setTableOrderId(response.data.order_id);
          showSuccess(`${item.name} added to cart!`);
          await fetchTableCartItems();
        } catch (error) {
          console.error("Error creating new order", error);
          showError("Failed to add item to cart");
        }
      } else {
        tableCartItem.order_id = currentOrderId;
        try {
          // console.log("Adding to existing order:", currentOrderId);
          await tableService.addNewItemInOrder(tableCartItem);
          showSuccess(`${item.name} added to cart!`);
          await fetchTableCartItems();
        } catch (error) {
          console.error("Error adding item to order", error);
          showError("Failed to add item to cart");
        }
      }
    } else {
      setSelectedItem(item);
    }
  };

  const handleAddToCartWithAddonsAndSizes = async (item, selectedSize, addons, specialInstructions) => {
    let isAdmin = JSON.parse(sessionStorage.getItem('user_id'))
    let tableCartItem = {
      table_id: tableData.table.id,
      outlet_id: tableData.outlet.id,
      menu_item_id: item.id,
      name: item.name,
      description: item.description,
      image_url: item.image_url,
      base_price: item.base_price,
      quantity: 1,
      total_price: item.base_price,
      is_veg: item.is_veg,
      category_id: item.category_id,
      discount_percentage: item.discount_percentage,
      order_type: orderTypeSelection.toUpperCase(),
      addons: addons,
      instructions: specialInstructions || null,
      status: isAdmin ? true : false
    };

    if (selectedSize) {
      tableCartItem["size_name"] = selectedSize.name;
      tableCartItem["size_price"] = selectedSize.price_modifier;
    }

    // console.log("Selected Size => ", selectedSize);
    // console.log("addons => ", addons);

    let currentOrderId = tableOrderId;
    let currentOrderStatus = tableOrderStatus;

    if (!currentOrderId || !currentOrderStatus) {
      // console.log("Order info missing, fetching fresh status...");
      const statusResponse = await tableService.tableOrderStatusCheck({ table_id: tableData.table.id });
      currentOrderStatus = statusResponse.data.ordering_status;
      currentOrderId = statusResponse.data.order_id;

      if (currentOrderId) {
        setTableOrderId(currentOrderId);
        setTableOrderStatus(currentOrderStatus);
      }
    }

    // console.log("currentOrderStatus => ", currentOrderStatus);
    // console.log("currentOrderId => ", currentOrderId);

    if (!currentOrderStatus) {
      try {
        // console.log("Creating new order...");
        const response = await tableService.newTableOrder(tableCartItem);
        setTableOrderStatus(true);
        setTableOrderId(response.data.order_id);
        await fetchTableCartItems();
        showSuccess(`${item.name} added to cart!`);
      } catch (error) {
        console.error("Error creating new order", error);
        showError("Failed to add item to cart");
      }
    } else {
      tableCartItem.order_id = currentOrderId;
      try {
        // console.log("Adding to existing order:", currentOrderId);
        await tableService.addNewItemInOrder(tableCartItem);
        await fetchTableCartItems();
        showSuccess(`${item.name} added to cart!`);
      } catch (error) {
        console.error("Error adding item to order", error);
        showError("Failed to add item to cart");
      }
    }
  };

  const handleRemoveItem = async (itemId, orderType) => {
    // console.log("Item Id => ", itemId)
    try {
      const currentItems = orderType === 'dinein' ? cartItems.dinein : cartItems.takeaway;
      const cartItem = currentItems.find(item => item.id === itemId);
      // console.log("Cart Item => ", cartItem);
      if (cartItem && tableOrderId) {
        await tableService.removeOrderItem({
          order_id: tableOrderId,
          menu_item_id: cartItem.menu_item_id,
          size_name: cartItem.size,
          addons: cartItem.addons,
          order_type: orderType
        });
        await fetchTableCartItems();

        const updatedItems = await fetchTableCartItems();
        const allItemsEmpty = updatedItems.dinein.length === 0 && updatedItems.takeaway.length === 0;
        if (allItemsEmpty) {
          setTableOrderStatus(false);
          setTableOrderId(null);
        }
      }
    } catch (error) {
      console.error("Error removing item", error);
      showError("Failed to remove item");
    }
  };

  const handleModalAddToCart = async (item, quantity, addons, specialInstructions, selectedSize) => {
    // console.log("Table Cart Items => ", cartItems);
    // console.log("Order Type Selection => ", orderTypeSelection);
    // console.log("Selected Addons => ", addons)
    const currentCartItems = orderTypeSelection === 'dinein' ? cartItems.dinein : cartItems.takeaway;

    if (!currentCartItems || currentCartItems.length === 0) {
      await handleAddToCartWithAddonsAndSizes(item, selectedSize, addons, specialInstructions);
      showSuccess(`${item.name} added to cart!`);
      setSelectedItem(null);
      return;
    }

    const existingItem = currentCartItems.find(cartItem => {
      if ((cartItem.menu_item_id || cartItem.id) !== item.id) return false;

      const sizeMatch = selectedSize
        ? cartItem.size === selectedSize.name
        : !cartItem.size;

      if (!sizeMatch) return false;

      const cartAddonIds = cartItem.addons?.map(a => a.id).sort().join(',') || '';
      const newAddonIds = addons?.map(a => a.id).sort().join(',') || '';

      if (cartAddonIds !== newAddonIds) return false;

      return true;
    });

    // console.log("Existing Item => ", existingItem);

    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      await handleQuantityChange(item.id, newQuantity, selectedSize, addons, orderTypeSelection);
      showSuccess(`${item.name} quantity updated in cart!`);
    } else {
      await handleAddToCartWithAddonsAndSizes(item, selectedSize, addons, specialInstructions);
      showSuccess(`${item.name} added to cart!`);
    }
    setSelectedItem(null);
  };

  const handleConfirmOrderItemsForUser = async () => {
    try {
      const response = await tableService.confirmOrderItems(tableOrderId)
      showSuccess("Order Confirmed Successfully")
      setConfirmItems(!confirmItems)
    }
    catch (error) {
      console.error("Error while confirm Order Items")
    }
  }

  const handleProceedToCheckout = (orderType) => {
    const itemsToCheckout = orderType === 'dinein' ? cartItems.dinein : cartItems.takeaway;

    if (itemsToCheckout.length === 0) {
      showNotification(`No ${orderType} items in cart`, 'error');
      return;
    }

    setCartDrawerOpen(false);
    navigate(`/checkout/${tableOrderId}`, {
      state: {
        cart: itemsToCheckout,
        cartTotal: itemsToCheckout.reduce((sum, item) => sum + ((item.price || item.base_price) * item.quantity), 0),
        tableData,
        tableOrderId,
        customerName,
        customerPhone,
        paymentMethod,
        specialInstructions,
        orderType: orderType,
      },
    });
  };
  

  const handleCloseBill = async (paymentData) => {
    let user_id = sessionStorage.getItem("user_id")
    try {
      let payload = {}
      if (paymentData?.selectedPayment !== "Cash" && paymentData?.amountReceived === "") {
        payload = {
          order_id: tableOrderId,
          payment_method: paymentData?.selectedPayment,
          amount_received: 0,
          reference: paymentReference
        }
      }
      else {
        payload = {
          order_id: tableOrderId,
          payment_method: paymentData?.selectedPayment,
          amount_received: parseFloat(paymentData?.amountReceived),
          reference: paymentReference
        }
      }



      if (user_id !== "undefined") {
        payload["admin_id"] = user_id
      }

      try {
        const response = await tableService.closeBill(payload);
        // if(error.response.status)
        // console.log("Response => ", response)
        showSuccess("Bill Closed Succcessfully")
        setTimeout(() => {
          window.close();
        }, 2000);
      }
      catch (error) {
        console.error("Error => ", error)
        showError(error?.message)
        setCartDrawerOpen(false);
        setPaymentModalOpen(false);
        return;
      }


      setCartDrawerOpen(false);
      setCartItems({ dinein: [], takeaway: [] });
      clearCart();
      setTableOrderStatus(false);
      setTableOrderId(null);
      setPaymentModalOpen(false);
      setSelectedPayment("");
      setAmountReceived("");
      setPaymentReference("")
      // navigate(`/tables/`)
    } catch (error) {
      console.error("Error closing bill", error);
      showError("Failed to close bill");
    }
  };

  const getPriceRange = () => {
    if (!menuItems.length) return [0, 100];
    const prices = menuItems.map((item) => item.base_price);
    return [Math.min(...prices), Math.max(...prices)];
  };

  const getFilteredMenuItems = () => {
    let items = [...menuItems];
    if (selectedCategories.length > 0)
      items = items.filter((item) => selectedCategories.includes(item.category_id));
    if (searchTerm)
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    items = items.filter(
      (item) => item.base_price >= priceRange[0] && item.base_price <= priceRange[1]
    );
    if (vegFilter !== 'all') {
      const isVeg = vegFilter === 'veg';
      items = items.filter((item) => item.is_veg === isVeg);
    }
    return items;
  };

  const filteredMenuItems = getFilteredMenuItems();

  const getMenuItemsByCategory = () => {
    const grouped = {};
    categories.forEach((category) => {
      grouped[category.id] = { ...category, items: [] };
    });
    filteredMenuItems.forEach((item) => {
      if (grouped[item.category_id]) grouped[item.category_id].items.push(item);
    });
    return grouped;
  };

  const menuItemsByCategory = getMenuItemsByCategory();

  const handleViewChange = (e, newView) => {
    if (newView !== null) setView(newView);
  };

  // Check if an item is in cart
  const isItemInCart = (itemId) => {
    const currentItems = orderTypeSelection === 'dinein' ? cartItems.dinein : cartItems.takeaway;
    return currentItems.some(item => item.menu_item_id === itemId || item.id === itemId);
  };

  const isItemInCartandItsStatus = (itemId) => {
    const currentItems = orderTypeSelection === 'dinein' ? cartItems.dinein : cartItems.takeaway;
    return currentItems.some(item => (item.menu_item_id === itemId || item.id === itemId) && item.status == true);
  };

  // Get item quantity from cart
  const getItemQuantity = (itemId) => {
    const currentItems = orderTypeSelection === 'dinein' ? cartItems.dinein : cartItems.takeaway;
    const item = currentItems.find(item => item.menu_item_id === itemId || item.id === itemId);
    return item?.quantity || 0;
  };

  // Waiter View Component
  const WaiterView = ({ categoryItems }) => {
    return (
      <Box sx={{ py: 1 }}>
        {categoryItems.map((item) => (
          <Paper
            key={`waiter-${item.id}`}
            variant="outlined"
            sx={{
              mb: 1,
              borderRadius: 2,
              width: { xs: '100%', sm: '100%', md: '100%' },
              p: { xs: '10px 12px', sm: '12px 14px' },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto', md: '1fr auto' },
              gridTemplateRows: 'auto auto',
              gap: '6px 12px',
              alignItems: 'center',
              '&:hover': { borderColor: 'grey.400' },
            }}
          >
            {/* Row 1 Left: Name + badges */}
            <Box sx={{ gridColumn: 1, gridRow: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.is_veg ? '#228B22' : '#B22222', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } }}>
                {item.name}
              </Typography>
              {item.is_spicy && <Whatshot sx={{ color: '#680000', fontSize: '0.9rem' }} />}
              {item.discount_enabled && item.discount_percentage > 0 && (
                <Chip label={`-${item.discount_percentage}%`} size="small"
                  sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#D32F2F', color: 'white' }} />
              )}
            </Box>

            {/* Row 2 Left: Veg badge + price + description */}
            <Box sx={{ gridColumn: 1, gridRow: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={item.is_veg ? 'Veg' : 'Non-Veg'}
                size="small"
                sx={{
                  fontSize: '0.7rem', height: 22,
                  bgcolor: item.is_veg ? '#e8f5e9' : '#ffebee',
                  color: item.is_veg ? '#2e7d32' : '#c62828',
                  fontWeight: 500,
                }}
              />
              {item.discount_enabled && item.discount_percentage > 0 ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#680000', display: 'inline', mr: 0.5, fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.85rem' } }}>
                    S$ {(item.base_price * (1 - item.discount_percentage / 100)).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#999', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' } }}>
                    S$ {item.base_price.toFixed(2)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.85rem' } }}>
                  S$ {item.base_price.toFixed(2)}
                </Typography>
              )}
              {item.description && (
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%', lineHeight: 1.4, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' } }}>
                  {item.description.length > 80 ? `${item.description.substring(0, 80)}...` : item.description}
                </Typography>
              )}
            </Box>

            {/* Rows 1–2 Right: Actions */}
            <Box sx={{ gridColumn: 2, gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              {isItemInCart(item.id) && getItemQuantity(item.id) > 0 ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small"
                      onClick={() => {
                        if (item.addons_with_details?.length === 0 && item.sizes_with_prices?.length === 0) {
                          setCartDrawerOpen(true)
                        } else {
                          setCartDrawerOpen(true)
                        }
                      }}
                      sx={{ bgcolor: '#680000', color: 'white', width: { xs: 24, sm: 28, md: 28 }, height: { xs: 24, sm: 28, md: 28 }, '&:hover': { bgcolor: '#520000' } }}>
                      <Remove sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.9rem' } }} />
                    </IconButton>
                    <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' }, fontWeight: 600, minWidth: 24, textAlign: 'center' }}>
                      {getItemQuantity(item.id)}
                    </Typography>

                    <IconButton size="small"
                      onClick={() => {
                        if (item.addons_with_details?.length === 0 && item.sizes_with_prices?.length === 0) {
                          handleQuantityChange(
                            item.id,
                            getItemQuantity(item.id) + 1,
                            null,
                            null,
                            orderTypeSelection
                          );
                        } else {
                          setSelectedItem(item);
                        }
                      }}
                      sx
                      ={{ bgcolor: '#680000', color: 'white', width: { xs: 24, sm: 28, md: 28 }, height: { xs: 24, sm: 28, md: 28 }, '&:hover': { bgcolor: '#520000' } }}>
                      <Add sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.9rem' } }} />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Button variant="contained" size="small"
                  onClick={() => handleAddToCart(item)}
                  sx={{ bgcolor: '#680000', textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' }, py: 0.5, px: { xs: 1.5, sm: 2, md: 2 }, borderRadius: 1.5, '&:hover': { bgcolor: '#520000' } }}>
                  Add
                </Button>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  // Cart Drawer Component
  const CartDrawerComponent = () => {
    // 'pending' or 'confirmed'

    const getFilteredCartItems = () => {
      if (activeCartTab === 'dinein') {
        return cartItems.dinein;
      } else if (activeCartTab === 'takeaway') {
        return cartItems.takeaway;
      }
      return [...cartItems.dinein, ...cartItems.takeaway];
    };

    const filteredItems = getFilteredCartItems();

    // Separate filtered items by confirmation status
    const confirmedItems = filteredItems.filter(item => item.status === true);
    const notConfirmedItems = filteredItems.filter(item => item.status === false);

    // Get items based on selected status tab
    const getDisplayedItems = () => {
      if (statusTab === 'confirmed') {
        return confirmedItems;
      } else {
        return notConfirmedItems;
      }
    };

    const displayedItems = getDisplayedItems();

    const calculateSubtotal = (items) => {
      return items.reduce((sum, item) => sum + ((item.price || item.base_price) * item.quantity), 0);
    };

    const calculateGST = (subtotal) => subtotal * 0.09;
    const calculateTotal = (subtotal) => subtotal * 1.09;

    const dineInSubtotal = calculateSubtotal(cartItems.dinein);
    const takeawaySubtotal = calculateSubtotal(cartItems.takeaway);
    const totalSubtotal = dineInSubtotal + takeawaySubtotal;
    const currentSubtotal = calculateSubtotal(filteredItems);

    return (
      <CartDrawer
        anchor="right"
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 3 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 450, md: 480 },
            borderRadius: { xs: 0, sm: '16px 0 0 16px' },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: '#680000',
            color: 'white',
            p: { xs: 1.5, sm: 2, md: 2 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '120%' }}>
            <ShoppingCart />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
              Your Cart
            </Typography>
            <Badge badgeContent={cartItems.dinein.length + cartItems.takeaway.length} color="error" sx={{ ml: 1 }}>
              <Box />
            </Badge>
          </Box>
          <IconButton onClick={() => setCartDrawerOpen(false)} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>

        {/* Order Type Tabs */}
        {(cartItems.dinein.length > 0 || cartItems.takeaway.length > 0) && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white', px: { xs: 1, sm: 2, md: 2 } }}>
            <Tabs
              value={activeCartTab}
              onChange={(e, newValue) => setActiveCartTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: { xs: 40, sm: 48, md: 48 },
                  fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                  '&.Mui-selected': {
                    color: '#680000',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#680000',
                },
              }}
            >
              <Tab
                sx={{
                  width: '50%',
                  '& .MuiTab-wrapper': {
                    width: '100%'
                  }
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 1, md: 1 }, width: '100%' }}>
                    <DiningIcon fontSize="small" />
                    <span>Dine In</span>
                    {cartItems.dinein.length > 0 && (
                      <Chip
                        label={cartItems.dinein.length}
                        size="small"
                        sx={{
                          height: { xs: 18, sm: 20, md: 20 },
                          minWidth: { xs: 18, sm: 20, md: 20 },
                          fontSize: '0.7rem',
                          backgroundColor: '#f44336',
                          color: 'white',
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    )}
                  </Box>
                }
                value="dinein"
              />
              <Tab
                sx={{
                  width: '50%',
                  '& .MuiTab-wrapper': {
                    width: '100%'
                  }
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 1, md: 1 }, width: '100%' }}>
                    <TakeawayIcon fontSize="small" />
                    <span>Takeaway</span>
                    {cartItems.takeaway.length > 0 && (
                      <Chip
                        label={cartItems.takeaway.length}
                        size="small"
                        sx={{
                          height: { xs: 18, sm: 20, md: 20 },
                          minWidth: { xs: 18, sm: 20, md: 20 },
                          fontSize: '0.7rem',
                          backgroundColor: '#f44336',
                          color: 'white',
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    )}
                  </Box>
                }
                value="takeaway"
              />
            </Tabs>
          </Box>
        )}

        {/* Status Tabs - Pending vs Confirmed */}
        {filteredItems.length > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white', px: { xs: 1, sm: 2, md: 2 } }}>
            <Tabs
              value={statusTab}
              onChange={(e, newValue) => setStatusTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: { xs: 40, sm: 48, md: 48 },
                  fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                  '&.Mui-selected': {
                    color: '#680000',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#680000',
                },
              }}
            >
              <Tab
                sx={{
                  width: '50%',
                  '& .MuiTab-wrapper': {
                    width: '100%'
                  }
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 1, md: 1 }, width: '100%' }}>
                    <CheckCircleOutline sx={{ fontSize: 20 }} />
                    <span>Confirmed</span>
                    {confirmedItems.length > 0 && (
                      <Chip
                        label={confirmedItems.length}
                        size="small"
                        sx={{
                          height: { xs: 18, sm: 20, md: 20 },
                          minWidth: { xs: 18, sm: 20, md: 20 },
                          fontSize: '0.7rem',
                          backgroundColor: '#4caf50',
                          color: 'white',
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    )}
                  </Box>
                }
                value="confirmed"
              />
              <Tab
                sx={{
                  width: '50%',
                  '& .MuiTab-wrapper': {
                    width: '100%'
                  }
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 1, md: 1 }, width: '100%' }}>
                    <Pending sx={{ fontSize: 20 }} />
                    <span>Pending</span>
                    {notConfirmedItems.length > 0 && (
                      <Chip
                        label={notConfirmedItems.length}
                        size="small"
                        sx={{
                          height: { xs: 18, sm: 20, md: 20 },
                          minWidth: { xs: 18, sm: 20, md: 20 },
                          fontSize: '0.7rem',
                          backgroundColor: '#ff9800',
                          color: 'white',
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    )}
                  </Box>
                }
                value="pending"
              />

            </Tabs>
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 2, md: 2 } }}>
          {cartLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#680000' }} />
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              {activeCartTab === 'dinein' ? (
                <>
                  <DiningIcon sx={{ fontSize: { xs: 56, sm: 72, md: 72 }, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
                    No Dine-in Items
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    Add items for dine-in from the menu
                  </Typography>
                </>
              ) : activeCartTab === 'takeaway' ? (
                <>
                  <TakeawayIcon sx={{ fontSize: { xs: 56, sm: 72, md: 72 }, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
                    No Takeaway Items
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    Add items for takeaway from the menu
                  </Typography>
                </>
              ) : (
                <>
                  <ShoppingCart sx={{ fontSize: { xs: 56, sm: 72, md: 72 }, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
                    Your cart is empty
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    Add items from the menu to get started
                  </Typography>
                </>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  if (activeCartTab === 'dinein') {
                    setOrderTypeSelection('dinein')
                  }
                  else {
                    setOrderTypeSelection('takeaway')
                  }
                  setCartDrawerOpen(false)
                }}
                sx={{ mt: 3, bgcolor: '#680000', '&:hover': { bgcolor: '#520000' }, fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' } }}
              >
                Browse Menu
              </Button>
            </Box>
          ) : displayedItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              {statusTab === 'pending' ? (
                <>
                  <Pending sx={{ fontSize: { xs: 56, sm: 72, md: 72 }, color: '#ff9800', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
                    No Pending Items
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    All items have been confirmed
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircleOutline sx={{ fontSize: { xs: 56, sm: 72, md: 72 }, color: '#4caf50', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
                    No Confirmed Items
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    Items will appear here once confirmed
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            displayedItems.map((item) => (
              <Paper
                key={item.id}
                sx={{
                  p: { xs: 1.5, sm: 2, md: 2 },
                  mb: 2,
                  borderRadius: 2,
                  border: statusTab === 'pending' ? '1px solid #ffe0b2' : '1px solid #c8e6c9',
                  bgcolor: statusTab === 'pending' ? '#fff8e1' : '#f1f8e9'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: item.is_veg ? '#228B22' : '#B22222',
                        }}
                      />
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#680000', fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } }}>
                        {item.name}
                      </Typography>
                      {/* {statusTab === 'confirmed' && (
                        <Chip
                          label="Confirmed"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: '#4caf50',
                            color: 'white',
                          }}
                        />
                      )} */}
                    </Box>

                    {item.size && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' } }}>
                        Size: <span style={{ color: 'white', backgroundColor: 'green', padding: 2, borderRadius: '6px', fontSize: '10px' }}>{item?.size}</span>
                      </Typography>
                    )}
                    {item.addons?.length > 0 &&
                      item.addons.map((addon, idx) => (
                        <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' } }}>
                          {addon.name} - S${(addon.price * addon.quantity).toFixed(2)}
                        </Typography>
                      ))}
                    {item.instructions && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' } }}
                      >
                        Note: {item.instructions}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 1,
                      minWidth: { xs: 80, sm: 90, md: 90 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } }}>
                        S${(item.total_price).toFixed(2)}
                      </Typography>
                      <IconButton
                        disabled={!sessionStorage.getItem("user_id") && item.status}
                        size="small"
                        onClick={() => handleRemoveItem(item.id, activeCartTab === 'dinein' ? 'dinein' : 'takeaway')}
                        sx={{ color: '#d32f2f', p: 0.25 }}
                      >
                        <DeleteOutline sx={{ fontSize: { xs: 16, sm: 17, md: 17 } }} />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <IconButton
                        disabled={!sessionStorage.getItem("user_id") && item.status}
                        size="small"
                        onClick={() => {
                          if (item.quantity > 1) {
                            handleQuantityChange(
                              item.menu_item_id || item.id,
                              item.quantity - 1,
                              item.size,
                              item.addons,
                              activeCartTab === 'dinein' ? 'dinein' : 'takeaway'
                            )
                          }
                          else {
                            handleRemoveItem(item.id, activeCartTab === 'dinein' ? 'dinein' : 'takeaway')
                          }
                        }}
                        sx={{
                          bgcolor: '#680000',
                          color: 'white',
                          width: { xs: 24, sm: 28, md: 28 },
                          height: { xs: 24, sm: 28, md: 28 },
                          '&:hover': { bgcolor: '#520000' },
                        }}
                      >
                        <Remove sx={{ fontSize: { xs: 12, sm: 14, md: 14 } }} />
                      </IconButton>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ minWidth: 20, textAlign: 'center', fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } }}
                      >
                        {item.quantity}
                      </Typography>
                      <IconButton
                        disabled={!sessionStorage.getItem("user_id") && item.status}
                        size="small"
                        onClick={() => handleQuantityChange(
                          item.menu_item_id || item.id,
                          item.quantity + 1,
                          item.size,
                          item.addons,
                          activeCartTab === 'dinein' ? 'dinein' : 'takeaway'
                        )}
                        sx={{
                          bgcolor: '#680000',
                          color: 'white',
                          width: { xs: 24, sm: 28, md: 28 },
                          height: { xs: 24, sm: 28, md: 28 },
                          '&:hover': { bgcolor: '#520000' },
                        }}
                      >
                        <Add sx={{ fontSize: { xs: 12, sm: 14, md: 14 } }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))
          )}
        </Box>

        {(cartItems.dinein.length > 0 || cartItems.takeaway.length > 0) && (
          <Box
            sx={{
              borderTop: '1px solid #e0e0e0',
              bgcolor: 'white',
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 2 },
                pt: { xs: 1.5, sm: 2, md: 2 },
                pb: 1,
                bgcolor: '#fafafa',
                borderBottom: '0.5px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                  Subtotal
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                  S${cartValues.cart_total?.toFixed(2)}
                </Typography>
              </Box>
              {cartValues.tax > 0 &&

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    Tax ({cartValues.tax}%)
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    S${cartValues.tax_value.toFixed(2)}
                  </Typography>
                </Box>

              }
              {cartValues.service_charge > 0 &&
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    Service Charge ({cartValues.service_charge}%)
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    S${cartValues.service_charge_value.toFixed(2)}
                  </Typography>
                </Box>

              }
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' } }}>
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#680000', fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
                  S${cartValues.total}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2, md: 2 }, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  handleConfirmOrderItemsForUser()
                }}
                disabled={notConfirmedItems.length === 0}
                startIcon={<CheckCircle />}
                sx={{
                  color: '#680000',
                  borderColor: '#680000',
                  py: { xs: 1, sm: 1.5, md: 1.5 },
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                  '&:hover': { bgcolor: '#fff1f1', borderColor: '#520000' },
                }}
              >
                Confirm Pending Items
              </Button>
            </Box>
            {localStorage.getItem("user_id") &&
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 2 }, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    if (notConfirmedItems.length > 0) {
                      showError("There are Some Pending Items in Cart , Please Remove it");
                      return;
                    }
                    setPaymentModalOpen(true)
                  }}
                  startIcon={<Receipt />}
                  sx={{
                    color: '#680000',
                    borderColor: '#680000',
                    py: { xs: 1, sm: 1.5, md: 1.5 },
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                    '&:hover': { bgcolor: '#fff1f1', borderColor: '#520000' },
                  }}
                >
                  Close Bill & Checkout
                </Button>
              </Box>}
          </Box>
        )}
      </CartDrawer>
    );
  };

  // Filter Sidebar Component
  const FilterSidebar = () => {
    const [localSelectedCategories, setLocalSelectedCategories] = useState([]);
    const [localPriceRange, setLocalPriceRange] = useState(priceRange);
    const [localVegFilter, setLocalVegFilter] = useState(vegFilter);

    useEffect(() => {
      if (filterOpen) {
        setLocalSelectedCategories(selectedCategories);
        setLocalPriceRange(priceRange);
        setLocalVegFilter(vegFilter);
      }
    }, [filterOpen, selectedCategories, priceRange, vegFilter]);

    const handleApplyFilters = () => {
      setSelectedCategories(localSelectedCategories);
      setPriceRange(localPriceRange);
      setVegFilter(localVegFilter);
      if (localSelectedCategories.length > 0) scrollToCategory(localSelectedCategories[0]);
      if (isMobile) setFilterOpen(false);
    };

    const handleClearFiltersLocal = () => {
      const defaultPriceRange = getPriceRange();
      setLocalSelectedCategories([]);
      setLocalPriceRange(defaultPriceRange);
      setLocalVegFilter('all');
      setSelectedCategories([]);
      setPriceRange(defaultPriceRange);
      setVegFilter('all');
      if (isMobile) setFilterOpen(false);
    };

    const handleCategoryChange = (categoryId) => {
      setLocalSelectedCategories((prev) =>
        prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
      );
    };

    const visibleCategories = categories.filter(
      (cat) => (menuItemsByCategory[cat.id]?.items || []).length > 0
    );

    const handleSelectAllCategories = () => {
      if (localSelectedCategories.length === visibleCategories.length) {
        setLocalSelectedCategories([]);
      } else {
        setLocalSelectedCategories(categories.map((cat) => cat.id));
      }
    };

    return (
      <Drawer
        anchor="left"
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 3 }}
        PaperProps={{
          sx: { width: { xs: '95vw', sm: 380, md: 400, lg: 420 }, borderRadius: 0 },
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }, fontWeight: 600 }}>
              Menu Filters
            </Typography>
            <IconButton onClick={() => setFilterOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleApplyFilters}
            sx={{
              height: { xs: 40, sm: 44, md: 48 },
              backgroundColor: '#680000',
              color: 'white',
              textTransform: 'none',
              borderRadius: '10px',
              mb: 2,
              fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
              '&:hover': { bgcolor: '#520000' }
            }}
          >
            Apply Filters
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={handleClearFiltersLocal}
            sx={{
              height: { xs: 40, sm: 44, md: 48 },
              color: '#680000',
              border: '1px solid #680000',
              textTransform: 'none',
              borderRadius: '10px',
              mb: 3,
              fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
              '&:hover': { color: '#520000' }
            }}
          >
            Clear All Filters
          </Button>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Accordion defaultExpanded sx={{ mb: 3, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }, fontWeight: 600 }}>
                    Categories
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#680000', fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                    {localSelectedCategories.length} selected
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Button
                  variant="text"
                  fullWidth
                  onClick={handleSelectAllCategories}
                  sx={{
                    textTransform: 'none',
                    color: '#680000',
                    fontWeight: 600,
                    mb: 2,
                    border: '1px solid #680000',
                    borderRadius: '6px',
                    py: 0.5,
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' },
                  }}
                >
                  {localSelectedCategories.length === visibleCategories.length ? 'Deselect All' : 'Select All'}
                </Button>
                <FormGroup>

                  {categories.map((category) => (

                    <FormControlLabel
                      key={category.id}
                      control={
                        <Checkbox
                          checked={localSelectedCategories.includes(category.id)}
                          onChange={() => handleCategoryChange(category.id)}
                          sx={{ color: '#680000', '&.Mui-checked': { color: '#680000' } }}
                        />
                      }
                      label={category.name}
                      sx={{ mb: 1, '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ mb: 3, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }, fontWeight: 600 }}>
                  Price Range
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ p: { xs: 1, sm: 2, md: 2 } }}>
                  <Slider
                    value={localPriceRange}
                    onChange={(e, newValue) => setLocalPriceRange(newValue)}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `S$${value}`}
                    min={getPriceRange()[0]}
                    max={getPriceRange()[1]}
                    sx={{ mt: 2, mb: 2, color: '#680000' }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' } }}>S${localPriceRange[0]}</Typography>
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' } }}>S${localPriceRange[1]}</Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ mb: 3, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }, fontWeight: 600 }}>
                  Food Type
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <RadioGroup value={localVegFilter} onChange={(e) => setLocalVegFilter(e.target.value)}>
                  <FormControlLabel
                    value="all"
                    control={<Radio sx={{ color: '#680000', '&.Mui-checked': { color: '#680000' } }} />}
                    label="All Items"
                    sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
                  />
                  <FormControlLabel
                    value="veg"
                    control={<Radio sx={{ color: '#680000', '&.Mui-checked': { color: '#680000' } }} />}
                    label="Vegetarian Only"
                    sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
                  />
                  <FormControlLabel
                    value="non-veg"
                    control={<Radio sx={{ color: '#680000', '&.Mui-checked': { color: '#680000' } }} />}
                    label="Non-Vegetarian Only"
                    sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
                  />
                </RadioGroup>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Drawer>
    );
  };

  // Mobile Categories Dialog
  const MobileCategoriesDialog = () => (
    <Dialog
      open={categoriesDialogOpen}
      onClose={() => setCategoriesDialogOpen(false)}
      fullScreen={isMobile}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          height: isMobile ? '80vh' : 'auto',
          maxHeight: isMobile ? '80vh' : '70vh',
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: '#680000', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: { xs: 1.5, sm: 2, md: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Category />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>Categories</Typography>
        </Box>
        <IconButton onClick={() => setCategoriesDialogOpen(false)} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <List sx={{ width: '100%' }}>
          {categories
            .filter((cat) => (menuItemsByCategory[cat.id]?.items || []).length > 0)
            .map((cat) => {
              const categoryItems = menuItemsByCategory[cat.id]?.items || [];
              return (
                <React.Fragment key={cat.id}>
                  <ListItem
                    button
                    onClick={() => scrollToCategory(cat.id)}
                    selected={selectedCategory === cat.id}
                    sx={{
                      py: { xs: 2, sm: 2.5, md: 2.5 },
                      px: { xs: 2, sm: 3, md: 3 },
                      borderLeft: selectedCategory === cat.id ? '4px solid #680000' : '4px solid transparent',
                      bgcolor: selectedCategory === cat.id ? '#FFF1F1' : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        component="img"
                        src={cat.image_url || 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?auto=format&fit=crop&w=800&q=60'}
                        alt={cat.name}
                        sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', boxShadow: 2 }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: selectedCategory === cat.id ? 700 : 500, color: selectedCategory === cat.id ? '#680000' : 'text.primary', fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' } }}>
                          {cat.name}
                        </Typography>
                      }
                      secondary={`${categoryItems.length} item${categoryItems.length !== 1 ? 's' : ''}`}
                    />
                    {selectedCategory === cat.id && <CheckCircle sx={{ color: '#680000', ml: 1 }} />}
                  </ListItem>
                  <Divider sx={{ my: 0 }} />
                </React.Fragment>
              );
            })}
        </List>
      </DialogContent>
    </Dialog>
  );

  // Floating Categories Button (Mobile)
  const FloatingCategoriesButton = () => {
    if (!isMobile) return null;
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: '#680000',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
          cursor: 'pointer',
        }}
        onClick={() => setCategoriesDialogOpen(true)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Menu />
          <Typography variant="subtitle1" fontWeight={500} sx={{ fontSize: '0.9rem' }}>Categories</Typography>
        </Box>
        {/* <Badge badgeContent={cartItems.dinein.length + cartItems.takeaway.length} color="error">
          <ShoppingCart sx={{ color: 'white' }} />
        </Badge> */}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress size={60} sx={{ color: '#680000' }} />
            {/* <Typography>Hello World</Typography> */}
          </Box>
        </Container>
      </Box>
    );
  }

  if (!tableData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Typography variant='h1' sx={{ color: '#680000' }}>Unable to access this table. Please contact staff</Typography>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/')} sx={{ bgcolor: '#680000', '&:hover': { bgcolor: '#520000' } }}>
                Back to Home Page
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  if (tableData.outlet.closed) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Typography variant='h1' sx={{ color: '#680000', mb: 1 }}>Sorry, Outlet Is Closed Today</Typography>
            <Typography variant='h5' sx={{ color: '#680000' }}>Please Come Back Tomorrow </Typography>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/')} sx={{ bgcolor: '#680000', '&:hover': { bgcolor: '#520000' } }}>
                Back to Home Page
              </Button>
            </Box>
          </Box>

        </Container>
      </Box>
    );
  }

  if (error || !tableData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Typography variant='h1' sx={{ color: '#680000' }}>QR Ordering is currently unavailable</Typography>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/')} sx={{ bgcolor: '#680000', '&:hover': { bgcolor: '#520000' } }}>
                Back to Home Page
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'white', minHeight: '100vh', display: 'flex', flexDirection: 'column', pb: isMobile ? 8 : 0 }}>
      <FloatingCategoriesButton />
      <MobileCategoriesDialog />
      <FilterSidebar />
      {CartDrawerComponent()}

      {/* Payment Modal - Integrated inside the component */}
      <Dialog
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedPayment("");
          setAmountReceived("");
          setBalance(""); // Reset balance when closing
        }}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2, md: 2 },
            maxWidth: 600,
            margin: { xs: 0, sm: '16px', md: '16px' },
            width: '100%'
          }
        }}
      >
        <DialogContent sx={{ p: { xs: 2, sm: 3, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: "#680000", fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' } }}>
              Select Payment Method
            </Typography>
            <IconButton
              onClick={() => {
                setPaymentModalOpen(false);
                setSelectedPayment("");
                setAmountReceived("");
                setBalance("");
              }}
              size="small"
              edge="end"
            >
              <Close />
            </IconButton>
          </Box>

          <RadioGroup
            value={selectedPayment}
            onChange={(e) => {
              setSelectedPayment(e.target.value);
              setAmountReceived("");
              setBalance("");
            }}
          >
            <FormControlLabel
              value="PayNow"
              control={
                <Radio
                  sx={{
                    color: "#680000",
                    "&.Mui-checked": { color: "#680000" }
                  }}
                />
              }
              label="PayNow"
              sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
            />

            <FormControlLabel
              value="Nets"
              control={
                <Radio
                  sx={{
                    color: "#680000",
                    "&.Mui-checked": { color: "#680000" }
                  }}
                />
              }
              label="Nets"
              sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
            />

            <FormControlLabel
              value="Visa/Mastercard"
              control={
                <Radio
                  sx={{
                    color: "#680000",
                    "&.Mui-checked": { color: "#680000" }
                  }}
                />
              }
              label="Visa / MasterCard"
              sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
            />

            {/* <FormControlLabel
              value="Master"
              control={
                <Radio
                  sx={{
                    color: "#680000",
                    "&.Mui-checked": { color: "#680000" }
                  }}
                />
              }
              label="Master"
              sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
            /> */}

            <FormControlLabel
              value="Cash"
              control={
                <Radio
                  sx={{
                    color: "#680000",
                    "&.Mui-checked": { color: "#680000" }
                  }}
                />
              }
              label="Cash"
              sx={{ '& .MuiTypography-root': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
            />
          </RadioGroup>

          {selectedPayment === "Cash" && (
            <Box sx={{ mt: 2 }}>
              {/* Show Total Amount */}
              <Box sx={{ mb: 2, p: { xs: 1.5, sm: 2, md: 2 }, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                  Total Bill Amount
                </Typography>
                <Typography variant="h5" sx={{ color: "#680000", fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' } }}>
                  S$ {cartValues.total?.toFixed(2) || "0.00"}
                </Typography>
              </Box>

              <TextField
                label="Amount Received"
                type="text" // Changed from "number" to "text" for better control
                fullWidth
                value={amountReceived}
                onChange={(e) => {
                  const inputValue = e.target.value;

                  // Allow empty string (when user clears the field)
                  if (inputValue === '') {
                    setAmountReceived('');
                    return;
                  }

                  // Validate: only numbers and one decimal point allowed
                  const decimalRegex = /^\d*\.?\d{0,2}$/;

                  if (decimalRegex.test(inputValue)) {
                    // Additional validation: ensure it doesn't start with decimal point
                    // and doesn't have multiple decimal points
                    if (inputValue === '.' || inputValue === '-.' || inputValue.startsWith('..')) {
                      return; // Invalid input, don't update state
                    }

                    // Check for multiple decimal points
                    const decimalCount = (inputValue.match(/\./g) || []).length;
                    if (decimalCount > 1) {
                      return; // Multiple decimals, don't update
                    }

                    // Check for negative sign in wrong position
                    if (inputValue.includes('-') && !inputValue.startsWith('-')) {
                      return; // Negative sign not at start, don't update
                    }

                    // If valid, update the state
                    setAmountReceived(inputValue);
                  }
                  // If regex test fails, don't update the state (invalid input)
                }}
                onBlur={(e) => {
                  // Final validation and cleanup on blur
                  const inputValue = e.target.value;

                  if (inputValue === '' || inputValue === '-' || inputValue === '.' || inputValue === '-.') {
                    setAmountReceived('');
                  } else if (inputValue.endsWith('.') || inputValue.endsWith('.0')) {
                    // Remove trailing decimal point for cleaner display
                    const numericValue = parseFloat(inputValue);
                    if (!isNaN(numericValue)) {
                      setAmountReceived(numericValue.toString());
                    }
                  }
                }}

                placeholder="Enter amount received"
                inputProps={{
                  step: "0.01",
                  min: "0"
                }}
                // Optional: Add error state for invalid inputs
                error={amountReceived !== '' && isNaN(parseFloat(amountReceived))}
                helperText={amountReceived !== '' && isNaN(parseFloat(amountReceived)) ? "Please enter a valid number" : ""}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': { borderColor: '#680000' },
                    borderRadius: 2
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#680000' },
                  '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1.2rem' } },
                  '& .MuiOutlinedInput-input': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1.5rem' } }
                }}
              />
              <Box sx={{ mt: 2, mb: 2, p: { xs: 1.5, sm: 2, md: 2 }, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" },
                  }}
                >
                  Balance to Return
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    color: parseFloat(balance) > 0 ? "#2e7d32" : "#d32f2f",
                    fontWeight: 700,
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.5rem" },
                  }}
                >
                  S$ {Number(balance || 0).toFixed(2)}
                </Typography>

                {parseFloat(amountReceived) < cartValues.total && amountReceived !== "" && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      mt: 1,
                      fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.75rem" },
                    }}
                  >
                    Amount received is less than bill amount
                  </Typography>
                )}
              </Box>

              {/* <TextField
                label="Balance to Return"
                type="text"
                fullWidth
                value={balance}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': { borderColor: '#680000' },
                    '& input': {
                      color: parseFloat(balance) > 0 ? '#2e7d32' : '#d32f2f',
                      fontWeight: 600
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#680000' },
                  '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' } },
                  '& .MuiOutlinedInput-input': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } }
                }}
                helperText={
                  parseFloat(amountReceived) < cartValues.total && amountReceived !== ""
                    ? "Amount received is less than bill amount"
                    : ""
                }
                FormHelperTextProps={{
                  sx: { color: '#d32f2f', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' } }
                }}
              /> */}
            </Box>
          )}

          <TextField
            label="Reference (optional)"
            // size="small"
            onChange={(e) => setPaymentReference(e.target.value)}
            fullWidth
            sx={{
              color: '#680000',
              mt: 2,
            }}
          />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setPaymentModalOpen(false);
                setSelectedPayment("");
                setAmountReceived("");
                setBalance("");
              }}
              sx={{
                borderColor: "#680000",
                color: "#680000",
                '&:hover': {
                  borderColor: "#680000",
                  backgroundColor: "#f5eaea"
                },
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                py: { xs: 0.5, sm: 0.75, md: 0.75 }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleCloseBill({ selectedPayment, amountReceived, balance })}
              disabled={
                !selectedPayment ||
                (selectedPayment === "Cash" && (
                  !amountReceived ||
                  amountReceived === "" ||
                  parseFloat(amountReceived) < cartValues.total
                ))
              }
              sx={{
                backgroundColor: "#680000",
                '&:hover': {
                  backgroundColor: "#4d0000"
                },
                '&.Mui-disabled': {
                  backgroundColor: "#cccccc"
                },
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                py: { xs: 0.5, sm: 0.75, md: 0.75 }
              }}
            >
              Close Bill
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row', md: 'row' },
        flexWrap: 'wrap',
        alignItems: { xs: 'flex-start', sm: 'center', md: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 1, md: 2 },
        px: { xs: 2, sm: 3, md: 4, lg: 7 },
        py: { xs: 2, sm: 1, md: 1 }
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: { xs: '100%', sm: 'auto', md: 'auto' }
        }}>
        {JSON.parse(localStorage.getItem("user_id")) && 
          <IconButton
            onClick={() => {
              navigate("/admin/table-addition") 
            }
              // window.close() } 
            }
            sx={{
              bgcolor: "#680000",
              color: "#fff",
              "&:hover": {
                bgcolor: "#4d0000",
              },
            }}
          >
            <ArrowBack />
          </IconButton> }
          <Box
            component="img"
            src={restaurantData.logo_url}
            alt={restaurantData.name}
            sx={{
              width: { xs: 50, sm: 60, md: 60 },
              height: { xs: 50, sm: 60, md: 60 },
              borderRadius: 1,
              objectFit: 'cover',
            }}
          />
          <Typography variant="h2" sx={{
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
            fontWeight: 'bold',
            wordBreak: 'break-word'
          }}>
            {restaurantData.name}
          </Typography>
        </Box>

        {/* Total Amount Section - Moved to the right side
        {cartValues.total && cartValues.total > 0 && (
          <Box sx={{
            textAlign: { xs: 'left', sm: 'right' },
            minWidth: { xs: 'auto', sm: '150px' }
          }}>
            <Typography variant='h3' sx={{
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              fontWeight: 'bold'
            }}>
              Total Amount
            </Typography>
            <Typography variant='h3' sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 'bold',
              color: '#680000'
            }}>
              S${cartValues.total}
            </Typography>
          </Box>
        )} */}
      </Box>

      {/* Dine-in/Takeaway Toggle Button Bar with Table Name and Username */}

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
        gap: { xs: 2, sm: 2, md: 3 },
        px: { xs: 2, sm: 3, md: 5, lg: 7 },
        width: '100%',
        mb: 2
      }}>
        {/* Toggle Buttons Section */}
        <Box sx={{
          textAlign: 'left',
          width: { xs: '100%', sm: '100%', md: '50%', lg: '45%' },
        }}>
          <ToggleButtonGroup
            value={orderTypeSelection}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setOrderTypeSelection(newValue);
              }
            }}
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                py: { xs: 1.2, sm: 1.2, md: 1.5 },
                px: { xs: 1.5, sm: 1.5, md: 2 },
                borderRadius: '12px !important',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', sm: '0.9rem', md: '1.1rem' },
                border: 'none',
                transition: 'all 0.3s ease',
                '&.Mui-selected': {
                  backgroundColor: '#680000',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#520000',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                },
                '&:not(.Mui-selected)': {
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#666',
                  },
                },
              },
            }}
          >
            <ToggleButton value="dinein" sx={{ flex: 1, gap: { xs: 0.5, sm: 0.5, md: 1 } }}>
              <DiningIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.1rem', md: '1.3rem' } }} />
              <Typography sx={{ display: { xs: 'none', sm: 'inline', md: 'inline' }, fontSize: { xs: '0.9rem', sm: '0.9rem', md: '1.1rem' } }}>Dine In</Typography>
              <Typography sx={{ display: { xs: 'inline', sm: 'none', md: 'none' }, fontSize: '0.9rem' }}>Dine In</Typography>
            </ToggleButton>
            <ToggleButton value="takeaway" sx={{ flex: 1, gap: { xs: 0.5, sm: 0.5, md: 1 } }}>
              <TakeawayIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.1rem', md: '1.3rem' } }} />
              <Typography sx={{ display: { xs: 'none', sm: 'inline', md: 'inline' }, fontSize: { xs: '0.9rem', sm: '0.9rem', md: '1.1rem' } }}>Takeaway</Typography>
              <Typography sx={{ display: { xs: 'inline', sm: 'none', md: 'none' }, fontSize: '0.9rem' }}>Takeaway</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' }, // Added wrapping
          ml: { xs: 0, sm: 0, md: 10 },
          gap: { xs: 2, sm: 2, md: 10 },
          alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
          justifyContent: { xs: 'flex-start', sm: 'space-between', md: 'flex-start' }, // Added space-between
          flex: 1,
          minWidth: 0,
          width: '100%', // Added full width
        }}>

          {/* Table Name Section */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'row', md: 'column' },
            alignItems: 'left',
            gap: 1.5,
            minWidth: { xs: 'auto', sm: 'auto', md: 0 },
            flex: { xs: '0 0 auto', sm: '0 0 auto', md: 1 }, // Changed flex behavior
            width: { xs: 'auto', sm: 'auto', md: 'auto' },
          }}>
            <Typography
              variant='h3'
              sx={{
                color: '#2e7d32',
                animation: 'blink 0.8s step-end infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0 }
                },
                fontWeight: 'bold',
                flexShrink: 0,
                // fontSize: { xs: '0.9rem', sm: '0.85rem', md: '1rem' },
                whiteSpace: 'nowrap', // Prevent label wrapping
              }}
            >
              Table:
            </Typography>
            <Typography
              variant='h3'
              sx={{
                color: '#2e7d32',
                animation: 'blink 0.8s step-end infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0 }
                },
                fontWeight: 'bold',
                maxWidth: { xs: '150px', sm: '120px', md: '250px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: { xs: '80px', sm: '80px', md: 'auto' }, // Added minWidth
                // fontSize: { xs: '0.9rem', sm: '0.85rem', md: '1rem' }
              }}
            >
              {tableName}
            </Typography>
          </Box>

          {/* Username Section */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'row', md: 'column' },
            alignItems: 'center',
            gap: 1.5,
            minWidth: { xs: 'auto', sm: 'auto', md: 0 },
            flex: { xs: '0 0 auto', sm: '0 0 auto', md: 1 }, // Changed flex behavior
            width: { xs: 'auto', sm: 'auto', md: 'auto' },
          }}>
            <Typography variant='h3' sx={{
              fontWeight: 'bold',
              color: '#1565c0',
              flexShrink: 0,
              // fontSize: { xs: '0.9rem', sm: '0.85rem', md: '1rem' },
              whiteSpace: 'nowrap', // Prevent label wrapping
            }}>
              Username:
            </Typography>
            <Typography variant='h3' sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              maxWidth: { xs: '150px', sm: '120px', md: '250px' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: { xs: '80px', sm: '80px', md: 'auto' }, // Added minWidth
              // fontSize: { xs: '0.9rem', sm: '0.85rem', md: '1rem' }
            }}>
              {adminName}
            </Typography>
          </Box>

          {/* Total Amount Section */}
          {cartValues.total > 0 &&
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'row', md: 'column' },
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
              ml: { xs: 0, sm: 'auto', md: 0 }, // Push to right on tablet
              width: { xs: 'auto', sm: 'auto', md: 'auto' },
            }}>
              <Typography variant='h3' sx={{
                fontWeight: 'bold',
                color: '#1565c0',
                // fontSize: { xs: '0.9rem', sm: '0.85rem', md: '1rem' },
                whiteSpace: 'nowrap', // Prevent label wrapping
              }}>
                Total:
              </Typography>
              <Typography variant='h3' sx={{
                fontWeight: 'bold',
                color: '#1a1a1a',
                // fontSize: { xs: '0.9rem', sm: '0.85rem', md: '1rem' },
                whiteSpace: 'nowrap', // Prevent wrapping
              }}>
                S${cartValues.total}
              </Typography>
            </Box>
          }
        </Box>
      </Box>

      {/* Search and Filter Section */}
      {categories.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: '1440px', margin: '0 auto', py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4 }, bgcolor: '#fff', display: 'flex', flexDirection: { xs: 'column', sm: 'row', md: 'row' }, gap: { xs: 2, sm: 3, md: 4 }, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder={`Search ${orderTypeSelection === 'dinein' ? 'dine-in' : 'takeaway'} dishes...`}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ backgroundColor: '#F9F9F9', borderRadius: '8px', flex: { xs: '1 1 auto', sm: '1 1 60%', md: '1 1 70%' }, '& .MuiOutlinedInput-root': { height: { xs: 48, sm: 48, md: 52 }, fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } } }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.4rem' }, color: '#999' }} /></InputAdornment>) }}
          />

          <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3, md: 3 }, alignItems: 'center', width: { xs: '100%', sm: 'auto', md: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start', md: 'flex-start' } }}>
            <Button variant="outlined" onClick={() => setFilterOpen(true)} startIcon={<TuneIcon />}
              sx={{ height: { xs: 48, sm: 48, md: 52 }, color: '#680000', border: '1px solid #680000', textTransform: 'none', borderRadius: '12px', fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' }, px: { xs: 2, sm: 3, md: 3 }, '&:hover': { borderColor: '#520000' } }}>
              Filters
            </Button>

            <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} sx={{ height: { xs: 48, sm: 48, md: 52 }, '& .MuiToggleButton-root': { height: '100%', width: { xs: 44, sm: 46, md: 48 }, border: '1px solid #680000', color: '#680000', '&.Mui-selected': { backgroundColor: '#680000', color: 'white' } } }}>
              <ToggleButton value="grid"><ViewModule sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.2rem' } }} /></ToggleButton>
              <ToggleButton value="list"><ViewList sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.2rem' } }} /></ToggleButton>
              {sessionStorage.getItem('user_id') && <ToggleButton value="waiter"><Person sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.2rem' } }} /></ToggleButton>}
            </ToggleButtonGroup>

            {/* <IconButton onClick={() => { fetchTableCartItems(); setCartDrawerOpen(true); }} sx={{ bgcolor: '#680000', color: 'white', '&:hover': { bgcolor: '#520000' }, width: { xs: 44, sm: 46, md: 48 }, height: { xs: 44, sm: 46, md: 48 } }}>
              <Badge badgeContent={cartItems.dinein.length + cartItems.takeaway.length} color="error">
                <ShoppingCart sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.2rem' } }} />
              </Badge>
            </IconButton> */}
            <Fab
              onClick={() => {
                fetchTableCartItems();
                setCartDrawerOpen(true);
              }}
              sx={{
                bgcolor: '#680000',
                color: 'white',
                '&:hover': { bgcolor: '#520000' },
                position: 'fixed',
                bottom: 50,
                right: 16,
                zIndex: 1000
              }}
            >
              <Badge
                badgeContent={cartItems.dinein.length + cartItems.takeaway.length}
                color="error"
              >
                <ShoppingCart />
              </Badge>
            </Fab>
          </Box>
        </Box>
      )}

      {/* Order Type Indicator Banner */}
      <Box sx={{ width: '100%', maxWidth: '1440px', margin: '0 auto', px: { xs: 2, sm: 3, md: 4 }, mb: 2 }}>
        <Paper
          sx={{
            p: { xs: 1, sm: 1.5, md: 1.5 },
            borderRadius: '12px',
            bgcolor: orderTypeSelection === 'dinein' ? '#e3f2fd' : '#fff3e0',
            border: `1px solid ${orderTypeSelection === 'dinein' ? '#1565c0' : '#e65100'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {orderTypeSelection === 'dinein' ? (
            <DiningIcon sx={{ color: '#1565c0', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.125rem' } }} />
          ) : (
            <TakeawayIcon sx={{ color: '#e65100', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.125rem' } }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 500, color: orderTypeSelection === 'dinein' ? '#1565c0' : '#e65100', fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
            You are currently ordering for <strong>{orderTypeSelection === 'dinein' ? 'Dine In' : 'Takeaway'}</strong>
            {orderTypeSelection === 'takeaway' && ' - Please collect your order from the counter'}
          </Typography>
        </Paper>
      </Box>

      {/* No Results */}
      {categories.length > 0 && filteredMenuItems.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, px: 2, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <Restaurant sx={{ fontSize: { xs: 60, sm: 80, md: 80 }, color: '#680000', opacity: 0.5, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#680000', fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' } }}>No Menu Items Found</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } }}>
            {searchTerm ? `No items found for "${searchTerm}". Try a different search term.` : 'No menu items match your current filters. Try adjusting your filters.'}
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      {categories.length > 0 && filteredMenuItems.length > 0 && (
        <Box sx={{ backgroundColor: '#fff', maxWidth: '1440px', margin: '0 auto', px: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Box sx={{ display: { xs: 'block', sm: 'flex', md: 'flex' }, gap: { xs: 2, sm: 3, md: 4 } }}>
            {/* Category Sidebar - Desktop (hide for waiter view on desktop) */}
            {!isMobile && view !== 'waiter' && (
              <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'flex' }, flexDirection: 'column', gap: 1.5, width: { xs: '100%', sm: '200px', md: '230px', lg: '250px' }, flexShrink: 0, p: 1, position: 'sticky', top: '100px', alignSelf: 'flex-start', height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
                {categories.filter((cat) => (menuItemsByCategory[cat.id]?.items || []).length > 0).map((cat) => (
                  <Paper key={cat.id} onClick={() => scrollToCategory(cat.id)} elevation={selectedCategory === cat.id ? 3 : 1} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1, sm: 1.5, md: 1.5 }, borderRadius: '14px', cursor: 'pointer', transition: 'all 0.3s ease', border: selectedCategory === cat.id ? '2px solid #680000' : '2px solid transparent', backgroundColor: selectedCategory === cat.id ? '#FFF1F1' : '#FAFAFA' }}>
                    <Box component="img" src={cat.image_url || 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?auto=format&fit=crop&w=800&q=60'} alt={cat.name} sx={{ width: { xs: 40, sm: 45, md: 45 }, height: { xs: 40, sm: 45, md: 45 }, borderRadius: '50%', objectFit: 'cover' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: selectedCategory === cat.id ? 700 : 500, fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' }, color: selectedCategory === cat.id ? '#680000' : 'text.primary' }}>{cat.name}</Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Menu Items */}
            <Box sx={{ flex: 1, height: '100vh' }}>
  {(() => {
    // Calculate the first visible category BEFORE the map loop
    const firstVisibleCategory = categories.find(
      (c) => (menuItemsByCategory[c.id]?.items || []).length > 0
    )?.id;

    return categories.map((category) => {
      const categoryItems = menuItemsByCategory[category.id]?.items || [];
      if (categoryItems.length === 0) return null;

      return (
        <LazyCategory
          key={category.id}
          categoryId={category.id}
          categoryName={category.name}
          isFirst={category.id === firstVisibleCategory}
          forceRender={forcedVisibleCategories.has(category.id)}
          categoryRefs={categoryRefs}
        >
          {view !== 'waiter' && (
            <Typography 
              variant="h5" 
              sx={{ 
                mt: { xs: 2, sm: 3, md: 4 }, 
                fontWeight: 700, 
                mb: { xs: 2, sm: 3, md: 4 }, 
                color: '#680000', 
                textTransform: 'uppercase', 
                letterSpacing: 1, 
                textAlign: 'center', 
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } 
              }}
            >
              {category.name}
            </Typography>
          )}
          
          {/* Grid View */}
          {view === 'grid' ? (
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              {categoryItems.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`grid-${item.id}`}>
                  <Card sx={{
                    width: { xs: '95vw', sm: '50vw', md: '95vw' },
                    borderRadius: '14px', 
                    boxShadow: 3, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    transition: 'all 0.3s ease', 
                    '&:hover': { transform: "translateY(-4px)", boxShadow: 6 },
                  }}>
                    <CardMedia 
                      component="div" 
                      sx={{ 
                        height: { xs: 150, sm: 160, md: 180 }, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        bgcolor: 'grey.100', 
                        backgroundImage: item.image_url ? `url(${buildImageUrl(item.image_url)})` : 'none', 
                        backgroundSize: 'cover', 
                        backgroundPosition: 'center' 
                      }}
                    >
                      {!item.image_url && <HideImage sx={{ fontSize: 40, color: 'grey.500' }} />}
                    </CardMedia>
                    
                    <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 1.5, md: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ 
                          fontSize: { xs: '0.9rem', sm: '0.85rem', md: '1rem' }, 
                          color: '#680000', 
                          fontWeight: 600, 
                          flex: 1, 
                          pr: 1 
                        }}>
                          {item.name}
                        </Typography>
                        <Box sx={{ 
                          width: 14, 
                          height: 14, 
                          border: `2px solid ${item.is_veg ? '#006400' : '#8B0000'}`, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          borderRadius: '2px' 
                        }}>
                          <Box sx={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            bgcolor: item.is_veg ? '#228B22' : '#B22222' 
                          }} />
                        </Box>
                        {item.is_spicy && <Whatshot sx={{ color: '#680000', fontSize: '1rem', ml: 0.5 }} />}
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="#444444" 
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.7rem', md: '0.75rem' }, 
                          mb: 1.5, 
                          lineHeight: 1.5, 
                          display: '-webkit-box', 
                          WebkitLineClamp: { xs: 2, sm: 2, md: 3 }, 
                          WebkitBoxOrient: 'vertical', 
                          overflow: 'hidden' 
                        }}
                      >
                        {item.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ 
                          fontSize: { xs: '0.85rem', sm: '0.85rem', md: '0.9rem' }, 
                          color: '#1A1A1A', 
                          fontWeight: 700 
                        }}>
                          {item.discount_enabled && item.discount_percentage > 0
                            ? `S$ ${(item.base_price * (1 - item.discount_percentage / 100)).toFixed(2)}`
                            : `S$ ${item.base_price.toFixed(2)}`}
                        </Typography>
                        {item.discount_enabled && item.discount_percentage > 0 && (
                          <Chip 
                            label={`${item.discount_percentage}% OFF`} 
                            size="small" 
                            sx={{ 
                              fontSize: '0.7rem', 
                              fontWeight: 600, 
                              backgroundColor: '#D32F2F', 
                              color: 'white' 
                            }} 
                          />
                        )}
                      </Box>

                      {isItemInCart(item.id) && getItemQuantity(item.id) > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                          <IconButton
                            disabled={!sessionStorage.getItem("user_id") && isItemInCartandItsStatus(item.id)}
                            sx={{ 
                              bgcolor: '#680000', 
                              color: '#FFF', 
                              width: 32, 
                              height: 32, 
                              '&:hover': { bgcolor: '#520000' } 
                            }} 
                            onClick={() => {
                              if (item.addons_with_details?.length === 0 && item.sizes_with_prices?.length === 0) {
                                setCartDrawerOpen(true)
                              } else {
                                setCartDrawerOpen(true)
                              }
                            }}
                          >
                            <Remove sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                            {getItemQuantity(item.id)}
                          </Typography>
                          <IconButton
                            disabled={!sessionStorage.getItem("user_id") && isItemInCartandItsStatus(item.id)}
                            sx={{ 
                              bgcolor: '#680000', 
                              color: '#FFF', 
                              width: 32, 
                              height: 32, 
                              '&:hover': { bgcolor: '#520000' } 
                            }} 
                            onClick={() => {
                              if (item.addons_with_details?.length === 0 && item.sizes_with_prices?.length === 0) {
                                handleQuantityChange(
                                  item.id,
                                  getItemQuantity(item.id) + 1,
                                  null,
                                  null,
                                  orderTypeSelection
                                );
                              } else {
                                setSelectedItem(item);
                              }
                            }}
                          >
                            <Add sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button 
                          variant="contained" 
                          fullWidth 
                          onClick={() => handleAddToCart(item)} 
                          sx={{ 
                            bgcolor: '#680000', 
                            textTransform: 'none', 
                            fontWeight: 'bold', 
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' }, 
                            mt: 1.5, 
                            height: { xs: 32, sm: 36, md: 36 }, 
                            '&:hover': { bgcolor: '#520000' } 
                          }}
                        >
                          Add to Cart
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : view === 'list' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: { xs: "100%", sm: "100%", md: '950px' } }}>
              {categoryItems.map((item) => (
                <Card key={`list-${item.id}`} sx={{
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row', md: 'row' }, 
                  borderRadius: '14px', 
                  boxShadow: 3, 
                  overflow: 'hidden', 
                  '&:hover': { transform: "translateY(-4px)", boxShadow: 6 },
                }}>
                  <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 140, md: 160 }, height: { xs: 160, sm: 140, md: 160 } }}>
                    <CardMedia 
                      component="div" 
                      sx={{ 
                        width: '100%', 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        bgcolor: 'grey.100', 
                        backgroundImage: item.image_url ? `url(${buildImageUrl(item.image_url)})` : 'none', 
                        backgroundSize: 'cover', 
                        backgroundPosition: 'center' 
                      }}
                    >
                      {!item.image_url && <HideImage sx={{ fontSize: 30, color: 'grey.500' }} />}
                    </CardMedia>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: { xs: 1.5, sm: 2, md: 2 } }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography sx={{ 
                          fontSize: { xs: '0.95rem', sm: '1rem', md: '1rem' }, 
                          color: '#680000', 
                          fontWeight: 600, 
                          flex: 1, 
                          pr: 2 
                        }}>
                          {item.name}
                        </Typography>
                        <Box sx={{ 
                          width: 16, 
                          height: 16, 
                          border: `2px solid ${item.is_veg ? '#006400' : '#8B0000'}`, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          borderRadius: '3px' 
                        }}>
                          <Box sx={{ 
                            width: 7, 
                            height: 7, 
                            borderRadius: '50%', 
                            bgcolor: item.is_veg ? '#228B22' : '#B22222' 
                          }} />
                        </Box>
                        {item.is_spicy && <Whatshot sx={{ color: '#680000', fontSize: '1rem', ml: 0.5 }} />}
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="#444" 
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' }, 
                          mb: 1.5 
                        }}
                      >
                        {item.description}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' }, 
                        color: '#1A1A1A', 
                        fontWeight: 600 
                      }}>
                        {item.discount_enabled && item.discount_percentage > 0
                          ? `S$ ${(item.base_price * (1 - item.discount_percentage / 100)).toFixed(2)}`
                          : `S$ ${item.base_price.toFixed(2)}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                      {isItemInCart(item.id) ? (
                        <>
                          <IconButton
                            disabled={!sessionStorage.getItem("user_id") && isItemInCartandItsStatus(item.id)}
                            sx={{ 
                              bgcolor: '#680000', 
                              color: '#fff', 
                              width: 34, 
                              height: 34, 
                              '&:hover': { bgcolor: '#520000' } 
                            }} 
                            onClick={() => {
                              if (item.addons_with_details?.length === 0 && item.sizes_with_prices?.length === 0) {
                                setCartDrawerOpen(true)
                              } else {
                                setCartDrawerOpen(true)
                              }
                            }}
                          >
                            <Remove sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                            {getItemQuantity(item.id)}
                          </Typography>
                          <IconButton
                            disabled={!sessionStorage.getItem("user_id") && isItemInCartandItsStatus(item.id)}
                            sx={{ 
                              bgcolor: '#680000', 
                              color: '#fff', 
                              width: 34, 
                              height: 34, 
                              '&:hover': { bgcolor: '#520000' } 
                            }} 
                            onClick={() => {
                              if (item.addons_with_details?.length === 0 && item.sizes_with_prices?.length === 0) {
                                handleQuantityChange(
                                  item.id,
                                  getItemQuantity(item.id) + 1,
                                  null,
                                  null,
                                  orderTypeSelection
                                );
                              } else {
                                setSelectedItem(item);
                              }
                            }}
                          >
                            <Add sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </>
                      ) : (
                        <Button 
                          variant="contained" 
                          onClick={() => handleAddToCart(item)} 
                          sx={{ 
                            bgcolor: '#680000', 
                            textTransform: 'none', 
                            fontWeight: 600, 
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' }, 
                            px: { xs: 2, sm: 2.5, md: 2.5 }, 
                            py: 0.75, 
                            borderRadius: '8px', 
                            '&:hover': { bgcolor: '#520000' } 
                          }}
                        >
                          Add to Cart
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          ) : (
            // Waiter View
            <WaiterView categoryItems={categoryItems} />
          )}
        </LazyCategory>
      );
    });
  })()}
</Box>
          </Box>
        </Box>
      )}

      {/* Menu Item Modal */}
      <MenuItemModal
        open={!!selectedItem}
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onAddToCart={handleModalAddToCart}
      />
    </Box>
  );
};

export default TableOrderPage;